"use client";

import { FocalPointImage } from "@/components/focal-point-image";
import { useMarqueeLoop } from "@/lib/animation";
import type { FooterProps } from "./footer.interface";

type Brewery = NonNullable<FooterProps["breweries"]>[number];

interface FooterBreweryMarqueeProps {
  breweries: Brewery[];
  /** Seconds for one full pass at the original CSS rate (100s for 2× content). */
  duration?: number;
}

/**
 * Footer brewery marquee — Motion infinite loop via `useMarqueeLoop`,
 * mirroring the header marquee but additionally pausing on hover/focus
 * so the user can actually focus on a logo they want to click.
 *
 * The content is rendered twice; the track animates `0% → -50%` so the
 * second copy sits exactly where the first started at end-of-loop,
 * making the reset invisible.
 */
export function FooterBreweryMarquee({
  breweries,
  duration = 100,
}: FooterBreweryMarqueeProps) {
  const { scope, pause, resume } = useMarqueeLoop({ duration });

  return (
    <div
      className="overflow-hidden"
      aria-hidden="true"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <div ref={scope} className="flex w-max items-center">
        {[...breweries, ...breweries].map((brewery, index) =>
          brewery.websiteUrl ? (
            <a
              key={index}
              href={brewery.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={-1}
              className="shrink-0 px-8 transition-opacity hover:opacity-70"
            >
              <FocalPointImage
                src={brewery.logoUrl}
                alt={brewery.logoAlt ?? `${brewery.name} logo`}
                width={100}
                height={100}
                className="mx-8 h-[78px] w-[78px] object-contain lg:h-[100px] lg:w-[100px]"
              />
            </a>
          ) : (
            <FocalPointImage
              key={index}
              src={brewery.logoUrl}
              alt={brewery.logoAlt ?? `${brewery.name} logo`}
              width={100}
              height={100}
              className="mx-8 h-[78px] w-[78px] object-contain lg:h-[100px] lg:w-[100px]"
            />
          ),
        )}
      </div>
    </div>
  );
}
